import React, { createContext, useContext, useMemo } from "react";
import { useProjectMessages } from "@/hooks/use-project-messages";
import type { ImagineUIMessage } from "@imagine/ai-service/shared-types";
import { imagineStore } from "@/stores/imagine.store";

interface MessageHistoryContextValue {
  messages: Array<ImagineUIMessage>;
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  fetchNextPage: () => Promise<unknown>;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  refetch: () => Promise<unknown>;
  isFinished: boolean;
}

const MessageHistoryContext = createContext<
  MessageHistoryContextValue | undefined
>(undefined);

interface MessageHistoryProviderProps {
  projectId: string;
  children: React.ReactNode;
  skipFetchingHistory?: boolean;
  disableMessageHistory?: boolean;
}

export function MessageHistoryProvider({
  projectId,
  children,
  skipFetchingHistory = false,
  disableMessageHistory = false,
}: MessageHistoryProviderProps) {
  const project = imagineStore.use.project();
  const isGenerating = imagineStore.use.chat.isGenerating();
  const history = imagineStore.use.chat.history();

  const {
    messages,
    total,
    pagination,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFinished,
    isLoading,
    isError,
    error,
    refetch,
  } = useProjectMessages({
    enabled:
      !disableMessageHistory &&
      !skipFetchingHistory &&
      Boolean(projectId) &&
      Boolean(project) &&
      !isGenerating &&
      !history,
  });

  const contextValue: MessageHistoryContextValue = useMemo(
    () => ({
      messages,
      total,
      hasMore: pagination?.hasMore ?? false,
      isLoading,
      isError,
      error: error as Error | null,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      refetch,
      isFinished,
    }),
    [
      messages,
      total,
      pagination?.hasMore,
      isLoading,
      isError,
      error,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      refetch,
      isFinished,
    ],
  );

  return (
    <MessageHistoryContext.Provider value={contextValue}>
      {children}
    </MessageHistoryContext.Provider>
  );
}

export function useMessageHistory() {
  const context = useContext(MessageHistoryContext);
  if (context === undefined) {
    throw new Error(
      "useMessageHistory must be used within a MessageHistoryProvider",
    );
  }
  return context;
}
